// src/merch/TemplatePicker.jsx
//
// Pick colours → pick a pattern → type name/number → Apply.
// Thumbnails are drawn live in the chosen colours, clipped to the sticker.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSnapshot } from "valtio";
import { merch, replaceLayers, setZoneLayers, mirrorLayer } from "./store";
import { printAreaFor } from "./products";
import { clearPaint } from "./paint";
import { PALETTES, PATTERNS, getPalette, drawPattern, buildTemplateLayers } from "./templates";
import { stickerPath2D } from "./shapes";

// Preview: each sticker (e.g. blade + shaft strip) stacked, clipped to its
// real shape. Text is a rough stand-in; real fonts render on apply.
function drawOne(ctx, sticker, x, y, W, H, patternId, pal, name, number) {
  const aspect = sticker.widthMm / sticker.heightMm;
  ctx.save();
  ctx.translate(x, y);
  ctx.clip(stickerPath2D(sticker, 1, 1, W - 2, H - 2));
  drawPattern(ctx, W, H, patternId, pal, { round: !!sticker.round });
  ctx.fillStyle = pal.text;
  ctx.strokeStyle = pal.outline;
  ctx.lineJoin = "round";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const drawT = (t, cx, cy, maxW, maxH) => {
    let px = maxH;
    ctx.font = `800 ${px}px Impact, "Arial Black", system-ui, sans-serif`;
    const w = ctx.measureText(t).width;
    if (w > maxW) px = (px * maxW) / w;
    ctx.font = `800 ${px}px Impact, "Arial Black", system-ui, sans-serif`;
    ctx.lineWidth = px * 0.18;
    ctx.strokeText(t, cx, cy);
    ctx.fillText(t, cx, cy);
  };
  if (aspect > 8) {
    if (name) drawT(name, W * 0.5, H / 2, W * 0.42, H * 0.6);
    if (number) drawT(number, W * 0.86, H / 2, W * 0.1, H * 0.7);
  } else if (aspect > 1.8) {
    if (number) drawT(number, W * 0.3, H * 0.52, W * 0.24, H * 0.6);
    if (name) drawT(name, W * 0.62, H * 0.46, W * 0.3, H * 0.3);
  } else {
    if (number) drawT(number, W / 2, H * 0.44, W * 0.5, H * 0.4);
    if (name) drawT(name, W / 2, H * 0.78, W * 0.62, H * 0.14);
  }
  ctx.restore();
}

function Thumb({ patternId, pal, stickers, name, number }) {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const W = 240, gap = 8;
    const hs = stickers.map((st) => Math.max(12, Math.round(W / (st.widthMm / st.heightMm))));
    const H = hs.reduce((a, b) => a + b, 0) + gap * (hs.length - 1);
    const dpr = window.devicePixelRatio || 1;
    cv.width = W * dpr; cv.height = H * dpr;
    cv.style.width = "100%";
    cv.style.aspectRatio = `${W} / ${H}`;
    const ctx = cv.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    let y = 0;
    stickers.forEach((st, i) => { drawOne(ctx, st, 0, y, W, hs[i], patternId, pal, name, number); y += hs[i] + gap; });
  }, [patternId, pal, stickers, name, number]);
  return <canvas ref={ref} />;
}

export default function TemplatePicker({ area, sticker, zones = [], activeId, onClose }) {
  const snap = useSnapshot(merch);
  const [paletteId, setPaletteId] = useState(PALETTES[0].id);
  const [patternId, setPatternId] = useState(PATTERNS[0].id);
  const [name, setName] = useState("");
  const [number, setNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const multi = zones.length > 1;
  const [scope, setScope] = useState(multi ? "all" : "one"); // whole product vs this part
  const pal = getPalette(paletteId);
  const zoneHas = (id) => (id === snap.side ? snap.layers.length : (snap.sideLayers[id] || []).length) > 0;
  const hasDesign = scope === "all" ? zones.some((z) => zoneHas(z.id)) || snap.paint.strokes > 0 : snap.layers.length > 0 || snap.paint.strokes > 0;
  // previews: one of each part (blade over shaft), or just this sticker
  const previewStickers = useMemo(() => {
    if (scope !== "all") return [sticker];
    const seen = new Set();
    const out = [];
    for (const z of [...zones].sort((a, b) => (a.part === "blade" ? -1 : 1) - (b.part === "blade" ? -1 : 1))) {
      if (z.face === "back" || seen.has(z.part)) continue;
      seen.add(z.part);
      out.push(z.sticker);
    }
    return out.length ? out : [sticker];
  }, [scope, zones, sticker]);

  async function apply() {
    setBusy(true);
    try {
      const opts = { patternId, paletteId, name: name.trim() || "YOUR NAME", number: number.trim() };
      clearPaint();
      merch.paint.active = false;
      if (scope === "all" && multi) {
        // fronts are laid out per part; each back mirrors its front so the
        // look wraps round the stick (text stays readable)
        const map = {};
        const fronts = zones.filter((z) => z.face !== "back");
        for (const z of fronts) {
          map[z.id] = await buildTemplateLayers({ ...opts, area: printAreaFor(z.sticker), sticker: z.sticker, part: z.part });
        }
        for (const z of zones.filter((z) => z.face === "back")) {
          const front = z.pair && map[z.pair];
          map[z.id] = front
            ? front.map((L) => mirrorLayer({ ...L, transform: { ...L.transform } }))
            : await buildTemplateLayers({ ...opts, area: printAreaFor(z.sticker), sticker: z.sticker, part: z.part });
        }
        setZoneLayers(map);
      } else {
        const cur = zones.find((z) => z.id === activeId);
        replaceLayers(await buildTemplateLayers({ ...opts, area, sticker, part: cur?.part }));
      }
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ma-modal" onPointerDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ma-modal-card ma-tpl" role="dialog" aria-label="Templates">
        <div className="ma-modal-head">
          <b>Templates</b>
          <button className="ma-btn ghost" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {multi && (
          <div className="ma-seg ma-tpl-scope" role="tablist" aria-label="Apply to">
            <button aria-pressed={scope === "all"} onClick={() => setScope("all")}>Whole stick</button>
            <button aria-pressed={scope === "one"} onClick={() => setScope("one")}>
              Just {zones.find((z) => z.id === activeId)?.label || "this part"}
            </button>
          </div>
        )}
        <h3>Colours</h3>
        <div className="ma-tpl-palettes">
          {PALETTES.map((p) => (
            <button
              key={p.id}
              className="ma-pal"
              title={p.label}
              aria-label={p.label}
              aria-pressed={p.id === paletteId}
              onClick={() => setPaletteId(p.id)}
              style={{ background: `linear-gradient(135deg, ${p.primary} 0 55%, ${p.secondary} 55% 85%, ${p.accent} 85%)` }}
            />
          ))}
        </div>
        <p className="ma-muted">{pal.label}</p>

        <div className="ma-tpl-fields">
          <label>Name<input value={name} maxLength={16} placeholder="YOUR NAME" onChange={(e) => setName(e.target.value.toUpperCase())} /></label>
          <label>Number<input value={number} maxLength={3} inputMode="numeric" placeholder="00" onChange={(e) => setNumber(e.target.value.replace(/[^0-9]/g, ""))} /></label>
        </div>

        <h3>Pattern</h3>
        <div className="ma-tpl-grid">
          {PATTERNS.map((p) => (
            <button key={p.id} className="ma-tpl-item" aria-pressed={p.id === patternId} onClick={() => setPatternId(p.id)}>
              <Thumb patternId={p.id} pal={pal} stickers={previewStickers} name={name || "YOUR NAME"} number={number || "00"} />
              <span>{p.label}</span>
            </button>
          ))}
        </div>

        <div className="ma-modal-foot">
          {hasDesign && <span className="ma-muted">{scope === "all" && multi ? "This replaces the design on every part." : "This replaces your current design."}</span>}
          <button className="ma-btn primary" disabled={busy} onClick={apply}>{busy ? "Applying…" : "Use this design"}</button>
        </div>
      </div>
    </div>
  );
}
